import { __assign } from 'tslib';
import * as React from 'react';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { classNames } from 'uitk-react-utils';
import styles from './uitk-sheet.css';
import { UitkSheetPositionTypes } from './types.esm.js';
import UitkSheetContext from './uitk-sheet-context.esm.js';
import { isValidUitkSheetPosition, attachHandlers, setElementFocus, detachHandlers, setFocus, defineSheetLayout } from './uitk-sheet-utilities.esm.js';

var getClassNameList = classNames.bind(styles);
var UitkPopoverSheet = function (props) {
    var _a, _b, _c, _d, _e, _f, _g;
    var _h = props.autoFocus, autoFocus = _h === void 0 ? true : _h, _j = props.autoPosition, autoPosition = _j === void 0 ? true : _j, children = props.children, className = props.className, _k = props.fullWidth, fullWidth = _k === void 0 ? false : _k, hasModal = props.hasModal, intersectionObserverRoot = props.intersectionObserverRoot, isVisible = props.isVisible, maxHeight = props.maxHeight, _l = props.position, passedInPosition = _l === void 0 ? { block: UitkSheetPositionTypes.TRAILING, inline: UitkSheetPositionTypes.LEADING } : _l, _m = props.applyBlockStartMargin, applyBlockStartMargin = _m === void 0 ? false : _m, _o = props.showOverTrigger, showOverTrigger = _o === void 0 ? false : _o, _p = props.returnFocusOnClose, returnFocusOnClose = _p === void 0 ? true : _p, triggerRef = props.triggerRef, _q = props.type, type = _q === void 0 ? 'popover' : _q, width = props.width;
    var blockPosition = passedInPosition.block && isValidUitkSheetPosition(passedInPosition.block)
        ? passedInPosition.block
        : UitkSheetPositionTypes.TRAILING;
    var inlinePosition = passedInPosition.inline && isValidUitkSheetPosition(passedInPosition.inline)
        ? passedInPosition.inline
        : UitkSheetPositionTypes.LEADING;
    // State hooks
    var _r = React.useState({
        x: 0,
        y: 0
    }), offset = _r[0], setOffset = _r[1];
    var _s = React.useState(false), animationHasEnded = _s[0], setAnimationHasEnded = _s[1];
    var prevOffsetStoreRef = React.useRef({ x: 0, y: 0 });
    var sheetRef = React.useRef(null);
    var intersectionObserverRootRef = React.useRef(null);
    var baseClassName = "uitk-".concat(type, "-sheet");
    var sheetClassNames = getClassNameList(baseClassName, className, "".concat(baseClassName, "-inline-pos-").concat(inlinePosition), "".concat(baseClassName, "-block-pos-").concat(blockPosition), (_a = {},
        _a["".concat(baseClassName, "-full-width")] = fullWidth,
        _a), (_b = {},
        _b["".concat(baseClassName, "-open")] = isVisible,
        _b), (_c = {},
        _c["".concat(baseClassName, "-has-intersection-root-el")] = autoPosition && intersectionObserverRoot,
        _c), (_d = {},
        _d["".concat(baseClassName, "-has-block-start-margin")] = blockPosition === UitkSheetPositionTypes.TRAILING && applyBlockStartMargin,
        _d));
    var intersectionObserver;
    var unobserveIntersectionObserver = React.useCallback(function () {
        if (autoPosition && sheetRef.current) {
            intersectionObserver && intersectionObserver.unobserve(sheetRef.current);
        }
    }, [autoPosition, intersectionObserver]);
    var onAnimationEndCallback = function (e) {
        // There are multiple animationEnd callbacks due to there being 2-3 animations applied, this logic ensures animationEndedStateCallback() is only called once.
        !animationHasEnded && e.animationName.match(/sheet-grow-/) && setAnimationHasEnded(true);
    };
    // This callback may appear to be doing something similar to adjustAlignment(), but it's actually only required for Safari desktop.
    // Safari desktop doesn't currently support the use of css variables inside keyframe animations. Therefore the updates to --menu-grow-x and --menu-grow-y have no impact in safari desktop.
    // In order to get the auto positioning working in safari desktop, we need to use transform: translate and set css variables for x and y to be used in the translate.
    // There's also a corresponding CSS "hack" to target safari desktop only ln 59 in uitk-menu.scss
    // The reason all of this is done once the animation is complete, is because if we do this during the animation, it causes some jankiness.
    var animationEndedStateCallback = React.useCallback(function () {
        // eslint-disable-next-line no-negated-condition
        var viewport = typeof document !== 'undefined' ? document.documentElement : null;
        if ((sheetRef === null || sheetRef === void 0 ? void 0 : sheetRef.current) && viewport) {
            var container = sheetRef.current;
            var windowInnerHeight = window.innerHeight;
            var containerBoundingClientRect = container.getBoundingClientRect();
            var viewportBoundingClientRect = viewport.getBoundingClientRect();
            var newOffsetX = 0;
            var newOffsetY = 0;
            if (containerBoundingClientRect.right > viewportBoundingClientRect.right) {
                newOffsetX = -Math.abs(Math.ceil(containerBoundingClientRect.right) - Math.ceil(viewportBoundingClientRect.right));
            }
            else if (containerBoundingClientRect.left < viewportBoundingClientRect.left) {
                newOffsetX = Math.abs(containerBoundingClientRect.left);
            }
            if (blockPosition === UitkSheetPositionTypes.TRAILING && containerBoundingClientRect.bottom > windowInnerHeight) {
                newOffsetY = -Math.abs(Math.ceil(containerBoundingClientRect.bottom) - Math.ceil(windowInnerHeight));
            }
            else if (blockPosition === UitkSheetPositionTypes.LEADING && containerBoundingClientRect.top < 0) {
                newOffsetY = Math.abs(Math.ceil(containerBoundingClientRect.top));
            }
            if (newOffsetX || newOffsetY) {
                container.style.setProperty('--uitk-popover-sheet-offset-translate-x', "".concat(newOffsetX, "px"));
                container.style.setProperty('--uitk-popover-sheet-offset-translate-y', "".concat(newOffsetY, "px"));
            }
            unobserveIntersectionObserver(); // Unobserve after the initial re-aligment, so that the popover sheet doesn't end up far away from the trigger on scroll
        }
    }, [blockPosition, unobserveIntersectionObserver]);
    var adjustAlignment = function (entry) {
        var prevOffset = prevOffsetStoreRef.current;
        var isIntersectingOnRight = Math.ceil(entry.boundingClientRect.right) > Math.ceil(entry.intersectionRect.right);
        var isIntersectingOnLeft = Math.ceil(entry.boundingClientRect.left) < Math.ceil(entry.intersectionRect.left);
        var isIntersectingOnBottom = Math.ceil(entry.boundingClientRect.bottom) > Math.ceil(entry.intersectionRect.bottom);
        var isIntersectingOnTop = Math.ceil(entry.boundingClientRect.top) < Math.ceil(entry.intersectionRect.top);
        if (isIntersectingOnRight) {
            var currentOffsetX = Math.ceil(entry.boundingClientRect.right) - Math.ceil(entry.intersectionRect.right);
            setOffset({
                x: -Math.abs(prevOffset.x - currentOffsetX),
                y: prevOffset.y
            });
        }
        else if (isIntersectingOnLeft) {
            var currentOffsetX = entry.boundingClientRect.left - entry.intersectionRect.left;
            setOffset({
                x: prevOffset.x - currentOffsetX,
                y: prevOffset.y
            });
        }
        if (isIntersectingOnBottom || isIntersectingOnTop) {
            var currentOffsetXRight = Math.ceil(entry.boundingClientRect.right) - Math.ceil(entry.intersectionRect.right);
            var currentOffsetXLeft = entry.boundingClientRect.left - entry.intersectionRect.left;
            var offsetX = prevOffset.x;
            // If it is also intersecting on left or right, we need to adjust offset on X axis too
            if (isIntersectingOnLeft) {
                offsetX = prevOffset.x - currentOffsetXLeft;
            }
            else if (isIntersectingOnRight) {
                offsetX = -Math.abs(prevOffset.x - currentOffsetXRight);
            }
            if (blockPosition === UitkSheetPositionTypes.TRAILING && isIntersectingOnBottom) {
                var currentOffsetY = Math.ceil(entry.boundingClientRect.bottom) - Math.ceil(entry.intersectionRect.bottom);
                setOffset({
                    x: offsetX,
                    y: -Math.abs(prevOffset.y - currentOffsetY)
                });
            }
            else if (blockPosition === UitkSheetPositionTypes.LEADING && isIntersectingOnTop) {
                var currentOffsetY = Math.ceil(entry.boundingClientRect.top) - Math.ceil(entry.intersectionRect.top);
                setOffset({
                    x: offsetX,
                    y: prevOffset.y - currentOffsetY
                });
            }
        }
    };
    var autoPositionCallback = function (entries) {
        entries.forEach(function (entry) {
            if (entry.intersectionRatio < 0.99 && entry.intersectionRatio > 0) {
                adjustAlignment(entry);
            }
        });
    };
    var autoPositionObserver = React.useCallback(function () {
        if (!(window && window.IntersectionObserver)) {
            return false;
        }
        if (intersectionObserverRoot) {
            intersectionObserverRootRef.current = document.querySelector(intersectionObserverRoot);
        }
        var thresholdSet = [];
        for (var i = 0; i <= 1.0; i += 0.01) {
            thresholdSet.push(i);
        }
        var defaultIntersectionObserverOptions = {
            threshold: thresholdSet
        };
        var intersectionObserverOptions = intersectionObserverRoot
            ? Object.assign(defaultIntersectionObserverOptions, {
                root: intersectionObserverRootRef.current
            })
            : defaultIntersectionObserverOptions;
        intersectionObserver = new IntersectionObserver(autoPositionCallback, intersectionObserverOptions);
        sheetRef.current && intersectionObserver.observe(sheetRef.current);
    }, [intersectionObserverRoot]);
    // Hooks
    React.useEffect(function () {
        if (isVisible) {
            attachHandlers(props, sheetRef);
            autoPosition && autoPositionObserver();
        }
    }, [autoFocus, autoPosition, autoPositionObserver, isVisible, hasModal, sheetRef, props]);
    // Set focus on mount
    React.useEffect(function () {
        if (autoFocus && isVisible) {
            setElementFocus(sheetRef, false, false);
        }
    }, [autoFocus, isVisible]);
    React.useEffect(function () {
        prevOffsetStoreRef.current = offset;
    }, [offset]);
    React.useEffect(function () {
        animationHasEnded && animationEndedStateCallback();
    }, [animationHasEnded, animationEndedStateCallback]);
    // "unmount" hook
    React.useEffect(function () {
        var _a, _b;
        if (!isVisible) {
            detachHandlers();
            unobserveIntersectionObserver();
            setOffset({ x: 0, y: 0 });
            setAnimationHasEnded(false);
            (_a = sheetRef === null || sheetRef === void 0 ? void 0 : sheetRef.current) === null || _a === void 0 ? void 0 : _a.style.removeProperty('--uitk-popover-sheet-offset-translate-x');
            (_b = sheetRef === null || sheetRef === void 0 ? void 0 : sheetRef.current) === null || _b === void 0 ? void 0 : _b.style.removeProperty('--uitk-popover-sheet-offset-translate-y');
            if (returnFocusOnClose) {
                setFocus(triggerRef);
            }
        }
    }, [isVisible, unobserveIntersectionObserver, returnFocusOnClose, triggerRef]);
    var _t = defineSheetLayout(children), hasCloseButton = _t.hasCloseButton, layoutProps = _t.layoutProps, closeButtonLayoutProps = _t.closeButtonLayoutProps, closeButtonElem = _t.closeButtonElem, childrenElem = _t.childrenElem;
    var posStyle = __assign(__assign(__assign(__assign({}, ((triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) &&
        triggerRef.current.offsetParent &&
        (sheetRef === null || sheetRef === void 0 ? void 0 : sheetRef.current) && __assign(__assign((_e = {}, _e["--uitk-popover-sheet-block-pos"] = blockPosition === UitkSheetPositionTypes.LEADING
        ? "".concat(triggerRef.current.offsetTop -
            (sheetRef.current.offsetHeight - (showOverTrigger ? triggerRef.current.offsetHeight : 0)), "px")
        : "".concat(triggerRef.current.offsetTop + (showOverTrigger ? 0 : triggerRef.current.offsetHeight), "px"), _e), (inlinePosition === UitkSheetPositionTypes.LEADING && (_f = {},
        _f["--uitk-popover-sheet-inline-pos-ltr"] = "".concat(triggerRef.current.offsetLeft, "px"),
        _f["--uitk-popover-sheet-inline-pos-rtl"] = "".concat(triggerRef.current.offsetParent.offsetWidth - triggerRef.current.offsetWidth - triggerRef.current.offsetLeft, "px"),
        _f))), (inlinePosition === UitkSheetPositionTypes.TRAILING && (_g = {},
        _g["--uitk-popover-sheet-inline-pos-ltr"] = "".concat(triggerRef.current.offsetLeft + triggerRef.current.offsetWidth - sheetRef.current.offsetWidth, "px"),
        _g["--uitk-popover-sheet-inline-pos-rtl"] = "".concat(triggerRef.current.offsetParent.offsetWidth -
            triggerRef.current.offsetWidth -
            triggerRef.current.offsetLeft -
            (sheetRef.current.offsetWidth - triggerRef.current.offsetWidth), "px"),
        _g))))), (width && {
        inlineSize: "".concat(width, "px")
    })), (offset && {
        '--uitk-popover-sheet-grow-x': "".concat(offset.x, "px"),
        '--uitk-popover-sheet-grow-y': "".concat(offset.y, "px")
    })), (autoPosition &&
        intersectionObserverRootRef.current && {
        '--uitk-popover-sheet-max-inline-size': "".concat(intersectionObserverRootRef.current.clientWidth, "px")
    }));
    return (React.createElement(UitkSheetContext.Provider, { value: { maxHeight: maxHeight, isVisible: isVisible, triggerRef: triggerRef, type: type } },
        React.createElement("section", { "data-testid": "popover-sheet", ref: sheetRef, className: sheetClassNames, tabIndex: -1, style: posStyle, onAnimationEnd: onAnimationEndCallback },
            hasCloseButton && (React.createElement(UitkLayoutFlex, __assign({}, layoutProps),
                React.createElement(UitkLayoutFlexItem, __assign({}, closeButtonLayoutProps),
                    React.createElement("div", null, closeButtonElem)),
                childrenElem && React.createElement(UitkLayoutFlexItem, null, childrenElem))),
            !hasCloseButton && childrenElem)));
};
UitkPopoverSheet.displayName = 'UitkPopoverSheet';

export { UitkPopoverSheet };
